import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Search, Upload, Check, Loader2, HardDrive } from "lucide-react";
import { Input } from "@/components/ui/input";
import { DriveDialog } from "@/components/creatives/DriveDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/creatives`;
const LIBS = [["white_bg", "Fundal alb"], ["boxes", "Cutii"], ["", "Încărcate de mine"]];

const Thumb = ({ url, on, toggle, testId, label }) => (
  <button data-testid={testId} onClick={toggle} title={label || ""} className={`relative flex flex-col overflow-hidden rounded-lg border-2 bg-white transition-transform hover:scale-[1.03] ${on ? "border-amber-400" : "border-transparent"}`}>
    <span className="relative block aspect-square w-full"><img src={url} alt="" className="absolute inset-1 h-[calc(100%-0.5rem)] w-[calc(100%-0.5rem)] object-contain" loading="lazy" /></span>
    {on && <span className="absolute right-1 top-1 rounded-full bg-amber-400 p-0.5 text-zinc-950"><Check className="h-3 w-3" /></span>}
    {label && <span className="block w-full truncate bg-zinc-100 px-1 py-0.5 text-[9px] text-zinc-700">{label}</span>}
  </button>
);

const combine = (list) => (!list.length ? null : list.length === 1 ? list[0]
  : { sku: list.map((p) => p.sku).join("+"), title: list.map((p) => p.title).join(" + "), images: list.flatMap((p) => p.images.slice(0, 4)), multi: true });

const Library = ({ selected, toggle, reloadKey }) => {
  const [lib, setLib] = useState("white_bg");
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const load = () => axios.get(`${API}/sources`, { params: { library: lib, q } }).then((r) => setItems(r.data.items || [])).catch(() => {});
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [lib, q, reloadKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const onUpload = async (e) => {
    setBusy(true);
    for (const f of Array.from(e.target.files || [])) {
      const fd = new FormData(); fd.append("file", f);
      try { const r = await axios.post(`${API}/upload`, fd); setItems((u) => [{ id: r.data.id, url: r.data.url, name: f.name }, ...u]); toggle(r.data.url); }
      catch (err) { toast.error(`${f.name}: ${err.response?.data?.detail || err.message}`); }
    }
    setBusy(false); e.target.value = "";
  };
  return (
    <div className="space-y-2" data-testid="source-library">
      <div className="flex flex-wrap items-center gap-2">
        {LIBS.map(([k, l]) => <button key={k || "own"} data-testid={`library-tab-${k || "own"}`} onClick={() => setLib(k)} className={`rounded-full px-3 py-1 text-xs ${lib === k ? "bg-zinc-100 text-zinc-950" : "text-zinc-400 hover:text-zinc-200"}`}>{l}</button>)}
        {lib === "" && <button data-testid="upload-photo-btn" onClick={() => fileRef.current?.click()} disabled={busy} className="ml-auto flex items-center gap-1.5 rounded-full border border-zinc-700 px-3 py-1 text-xs text-zinc-200 hover:border-amber-400">
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />} Încarcă poze</button>}
        <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={onUpload} data-testid="upload-photo-input" />
      </div>
      {lib && <Input data-testid="library-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Caută după numele pozei sau al folderului" className="h-8 text-xs" />}
      <div className="max-h-80 overflow-y-auto pr-1">
        <div className="grid grid-cols-4 content-start gap-2 sm:grid-cols-6">
        {items.map((u) => <Thumb key={u.id} url={`${u.url}?w=300`} label={lib ? (u.folder || u.name) : ""} on={selected.includes(u.url)} toggle={() => toggle(u.url)} testId={`upload-image-${u.id}`} />)}
        </div>
      </div>
      {!items.length && <p className="text-xs text-zinc-500" data-testid="library-empty">{lib ? "Nicio poză încă — apasă „Google Drive” și importă folderul." : "Nicio poză încărcată."}</p>}
    </div>
  );
};

export const SourcePicker = ({ product, setProduct, selected, setSelected }) => {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [picked, setPicked] = useState([]);
  const [drive, setDrive] = useState(new URLSearchParams(window.location.search).has("drive"));
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    const s = new URLSearchParams(window.location.search).get("drive");
    if (s === "ok") toast.success("Google Drive conectat");
    if (s === "error") toast.error("Conectarea Google Drive a eșuat: " + (new URLSearchParams(window.location.search).get("msg") || ""));
    if (s) window.history.replaceState({}, "", window.location.pathname);
  }, []);
  useEffect(() => {
    const t = setTimeout(() => axios.get(`${API}/products`, { params: { q } }).then((r) => setResults(r.data.items || [])).catch(() => {}), 300);
    return () => clearTimeout(t);
  }, [q]);

  const pickProduct = (p) => {
    const next = picked.some((x) => x.sku === p.sku) ? picked.filter((x) => x.sku !== p.sku) : picked.length >= 4 ? picked : [...picked, p];
    setPicked(next); setProduct(combine(next));
  };
  const toggle = (url) => setSelected((s) => (s.includes(url) ? s.filter((x) => x !== url) : s.length >= 6 ? s : [...s, url]));

  return (
    <div className="space-y-4 rounded-xl border border-zinc-800 bg-zinc-900/50 p-5" data-testid="source-picker">
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-base font-semibold text-zinc-100">1. Alege produsele și pozele</h2>
        <button data-testid="drive-open-btn" onClick={() => setDrive(true)} className="ml-auto inline-flex items-center gap-1.5 rounded-full border border-zinc-700 px-3 py-1 text-xs text-zinc-200 hover:border-amber-400"><HardDrive className="h-3.5 w-3.5" /> Google Drive</button>
        <span className="text-xs text-zinc-400" data-testid="selected-count">{selected.length}/6 poze</span>
      </div>
      <div className="relative">
        <Search className="absolute left-3 top-2.5 h-4 w-4 text-zinc-500" />
        <Input data-testid="product-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Caută produs după nume sau SKU — poți alege până la 4 produse" className="pl-9" />
      </div>
      <div className="flex max-h-28 flex-wrap gap-2 overflow-y-auto">
        {[...picked, ...results.filter((p) => !picked.some((x) => x.sku === p.sku))].map((p) => {
          const on = picked.some((x) => x.sku === p.sku);
          return <button key={p.sku} data-testid={`product-option-${p.sku}`} onClick={() => pickProduct(p)} className={`rounded-full border px-3 py-1 text-xs transition-colors ${on ? "border-amber-400 bg-amber-400/10 text-amber-200" : "border-zinc-700 text-zinc-300 hover:border-zinc-500"}`}>
            {on && "✓ "}{p.sku} · {p.title.slice(0, 40)}</button>;
        })}
      </div>
      {picked.length > 1 && <p className="text-[11px] text-amber-300" data-testid="multi-product-note">{picked.length} produse — vor apărea toate împreună în aceeași creativă. Alege poze pentru fiecare produs.</p>}
      {picked.map((p) => (
        <div key={p.sku}>
          <p className="mb-2 text-xs text-zinc-400">Poze din Shopify — {p.title}</p>
          <div className="grid grid-cols-4 gap-2 sm:grid-cols-6">
            {p.images.map((u, i) => <Thumb key={u} url={u} on={selected.includes(u)} toggle={() => toggle(u)} testId={`shopify-image-${p.sku}-${i}`} />)}
          </div>
        </div>
      ))}
      <Library selected={selected} toggle={toggle} reloadKey={reloadKey} />
      {drive && <DriveDialog onClose={() => setDrive(false)} onImported={() => setReloadKey((k) => k + 1)} />}
    </div>
  );
};
